import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CalendarBlank,
  CalendarCheck,
  CaretDown,
  ClockCountdown,
  FunnelSimple,
  Gear,
  ListBullets,
  MapPin,
  MagnifyingGlass,
  Plus,
  SignOut,
  SquaresFour,
  Stack,
  Star,
  UsersThree,
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { getEventTypeConfig, getSupportedEventTypes, normalizeEventNameList } from '../../config/eventTypes';
import { isManagingEvent } from '../../lib/eventRelationship';
import { resolveMediaUrl } from '../../utils/media';
import api from '../../services/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import './EventHome.css';

const DEFAULT_EVENT_COVERS = {
  wedding: '/assets/event-covers/wedding.jpg',
  corporate: '/assets/event-covers/corporate.jpg',
  baby_shower: '/assets/event-covers/baby-shower.jpg',
  birthday: '/assets/event-covers/birthday.jpg',
};

const EVENT_TAGLINES = [
  'Every event is a new beginning.',
  'Beautiful moments become lasting memories.',
  'The best stories begin together.',
  'Celebrate today. Remember it forever.',
  'Every detail tells part of the story.',
  'Good times deserve beautiful settings.',
  'Gather, celebrate, and make it unforgettable.',
  'Little moments make the biggest memories.',
  'Joy grows when people come together.',
  'A special day, thoughtfully brought to life.',
  'Make room for moments that matter.',
  'The celebration starts right here.',
  'Thoughtful details. Unforgettable experiences.',
  'Where every gathering becomes a story.',
  'Today is worth celebrating beautifully.',
  'Bring people together. Let joy take over.',
  'Great celebrations live on in memory.',
  'A meaningful moment deserves its own magic.',
  'Plan the moment. Enjoy the memory.',
  'Every occasion can feel extraordinary.',
];

const getEventTagline = (index) => EVENT_TAGLINES[index % EVENT_TAGLINES.length];

const getEventLocation = (event) => {
  const venue = event?.venues?.[0];
  return [venue?.name, venue?.address].filter(Boolean).join(', ') || 'Venue to be announced';
};

const isUpcomingEvent = (event) => {
  if (!event?.start_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(event.start_date) >= today;
};

const isPastEvent = (event) => {
  const finalDate = event?.end_date || event?.start_date;
  if (!finalDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(finalDate) < today;
};

const getEventDetail = (event, typeLabel) => {
  if (event?.description) return event.description;
  const honoreeNames = event?.event_type === 'birthday'
    ? normalizeEventNameList(event?.honoree_names)
    : (event?.honoree_names || []);
  const people = [event?.bride_name, event?.groom_name, ...honoreeNames, ...(event?.host_names || [])]
    .filter(Boolean);
  if (people.length) return `Celebrating ${people.slice(0, 3).join(' & ')}`;
  return `${typeLabel} · ${isManagingEvent(event) ? 'Managed by you' : 'You are attending'}`;
};

const getDefaultCover = (eventType) => (
  DEFAULT_EVENT_COVERS[eventType] || DEFAULT_EVENT_COVERS.wedding
);

const getEventCover = (event) => {
  const heroMedia = event?.guest_experience?.hero_media || [];
  const heroImage = heroMedia.find((item) => (
    item?.visible !== false && (
      item?.media_type === 'image'
      || item?.type === 'image'
      || (!item?.media_type && !item?.type)
    )
  ));

  return resolveMediaUrl(
    event?.guest_experience?.couple_photo_url
      || heroImage?.url
      || heroImage?.media_url
      || event?.wedding_photos_preview?.[0]
  ) || getDefaultCover(event?.event_type);
};

const getEventPath = (event) => {
  if (event?.viewer_role === 'manager') return '/dashboard';
  if (event?.viewer_role === 'staff') return '/dashboard/tasks';
  if (event?.viewer_role === 'hotel_manager') return '/dashboard/rooms?tab=arrivals';
  if (event?.viewer_role === 'bride' || event?.viewer_role === 'groom') return '/couple';
  if (event?.viewer_role === 'host') return '/host';
  return '/guest';
};

const formatEventDate = (startDate, endDate) => {
  if (!startDate) return 'Dates to be announced';
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  const options = { day: 'numeric', month: 'short', year: 'numeric' };
  const startLabel = start.toLocaleDateString('en-IN', options);

  if (!end || start.toDateString() === end.toDateString()) return startLabel;
  return `${startLabel} – ${end.toLocaleDateString('en-IN', options)}`;
};

const scrollPageToTop = () => {
  const root = document.documentElement;
  const previousScrollBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, 0);
  root.style.scrollBehavior = previousScrollBehavior;
};

export default function EventHome({ autoOpenLastEvent = true }) {
  const { user, switchEvent, logout } = useAuth();
  const navigate = useNavigate();
  const homeRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('managing');
  const [timingFilter, setTimingFilter] = useState('upcoming');
  const [query, setQuery] = useState('');
  const [eventType, setEventType] = useState('all');
  const [layoutMode, setLayoutMode] = useState(() => {
    if (typeof window === 'undefined') return 'list';
    const savedLayout = window.localStorage.getItem('event_home_layout');
    return savedLayout === 'grid' ? 'grid' : 'list';
  });
  const [openingEventId, setOpeningEventId] = useState('');

  useEffect(() => {
    let active = true;

    const loadEvents = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get('/events');
        if (!active) return;
        const nextEvents = response.data || [];
        const hasManagingEvents = nextEvents.some(isManagingEvent);

        if (autoOpenLastEvent && !hasManagingEvents && nextEvents.length) {
          const savedEventId = window.localStorage.getItem('current_event_id');
          const lastOpenedEvent = nextEvents.find((event) => event.id === savedEventId) || nextEvents[0];
          const selectedEvent = await switchEvent(lastOpenedEvent.id);
          if (active && selectedEvent) {
            scrollPageToTop();
            navigate(getEventPath(selectedEvent), { replace: true });
          }
          return;
        }

        setEvents(nextEvents);
        setRelationshipFilter(nextEvents.some(isManagingEvent) ? 'managing' : 'attending');
        setTimingFilter(nextEvents.some(isUpcomingEvent) ? 'upcoming' : 'past');
      } catch (requestError) {
        if (active) setError(requestError.response?.data?.detail || 'Could not load your events.');
      } finally {
        if (active) setLoading(false);
      }
    };

    if (user?.id) loadEvents();
    return () => { active = false; };
  }, [autoOpenLastEvent, navigate, switchEvent, user?.id]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let frameId = 0;
    const updateDecorations = () => {
      frameId = 0;
      if (!homeRef.current) return;
      homeRef.current.style.setProperty('--decor-shift-slow', `${window.scrollY * 0.68}px`);
      homeRef.current.style.setProperty('--decor-shift-medium', `${window.scrollY * 0.52}px`);
    };
    const handleScroll = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateDecorations);
    };
    updateDecorations();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  const searchableEvents = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return events.filter((event) => {
      const matchesType = eventType === 'all' || event.event_type === eventType;
      const typeLabel = getEventTypeConfig(event.event_type)?.label || event.event_type;
      const matchesQuery = !normalizedQuery || [event.name, event.description, typeLabel]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));
      return matchesType && matchesQuery;
    });
  }, [eventType, events, query]);

  const matchesActiveTiming = timingFilter === 'upcoming' ? isUpcomingEvent : isPastEvent;
  const matchesActiveRelationship = relationshipFilter === 'managing'
    ? isManagingEvent
    : (event) => !isManagingEvent(event);
  const managingCount = searchableEvents.filter((event) => isManagingEvent(event) && matchesActiveTiming(event)).length;
  const attendingCount = searchableEvents.filter((event) => !isManagingEvent(event) && matchesActiveTiming(event)).length;
  const upcomingCount = searchableEvents.filter((event) => matchesActiveRelationship(event) && isUpcomingEvent(event)).length;
  const pastCount = searchableEvents.filter((event) => matchesActiveRelationship(event) && isPastEvent(event)).length;
  const filteredEvents = searchableEvents.filter((event) => (
    matchesActiveRelationship(event) && matchesActiveTiming(event)
  ));

  const handleOpenEvent = async (event) => {
    setOpeningEventId(event.id);
    const selected = await switchEvent(event.id);
    setOpeningEventId('');
    if (selected) {
      scrollPageToTop();
      navigate(getEventPath(selected));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleLayoutModeChange = (nextLayout) => {
    setLayoutMode(nextLayout);
    window.localStorage.setItem('event_home_layout', nextLayout);
  };

  return (
    <main className="event-home" ref={homeRef}>
      <div className="event-home__decorations" aria-hidden="true">
        <span className="event-home__shape event-home__shape--one" />
        <span className="event-home__shape event-home__shape--two" />
        <span className="event-home__shape event-home__shape--three" />
        <span className="event-home__shape event-home__shape--four" />
        <span className="event-home__shape event-home__shape--five" />
        <span className="event-home__shape event-home__shape--six" />
        <span className="event-home__shape event-home__shape--seven" />
        <span className="event-home__shape event-home__shape--eight" />
        <span className="event-home__shape event-home__shape--nine" />
        <span className="event-home__shape event-home__shape--ten" />
        <span className="event-home__shape event-home__shape--eleven" />
        <span className="event-home__shape event-home__shape--twelve" />
      </div>
      <header className="event-home__header">
        <Link to="/home" className="event-home__brand" aria-label="GalaGo Home">
          <img src="/assets/logo.png" alt="" />
          <strong>GalaGo</strong>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="event-home__account" title={user?.name || 'Account'} aria-label="Open account menu">
              <span>{(user?.name || 'U').slice(0, 2).toUpperCase()}</span>
              <div>
                <strong>{user?.name || 'Event manager'}</strong>
                <small>Event manager</small>
              </div>
              <CaretDown className="event-home__account-caret" weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={() => navigate('/account/showcase')}>
              <Star className="mr-2 h-4 w-4" />
              Showcase
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/account/settings')}>
              <Gear className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={handleLogout}
              data-testid="home-logout-btn"
            >
              <SignOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <section className="event-home__content">
        <div className="event-home__intro">
          <div className="event-home__intro-copy">
            <p className="event-home__intro-kicker">Events made easy</p>
            <h1>
              Hello
              {user?.name && <>, <span>{user.name.split(' ')[0]}</span></>}
            </h1>
            <p className="event-home__intro-tagline">Plan beautifully. Celebrate effortlessly.</p>
          </div>
          <div className="event-home__intro-visual" aria-hidden="true">
            <div className="event-home__intro-quote">Good events<br />create great<br />stories <span>♥</span></div>
            <img src="/assets/event-covers/home-header-multi-event-v1.png" alt="" />
          </div>
        </div>

        {/* <div className="event-home__event-heading">
          <p className="event-home__eyebrow">Your events <span>{loading ? '–' : events.length}</span></p>
        </div> */}

        <div className="event-home__analytics" aria-label="Filter events">
          <fieldset className="event-home__filter-group">
            <legend>Your role</legend>
            <div>
              <button type="button" className={`event-home__analytics-card ${relationshipFilter === 'managing' ? 'is-active' : ''}`} onClick={() => setRelationshipFilter('managing')} aria-pressed={relationshipFilter === 'managing'}>
                <span><CalendarCheck size={23} weight="fill" /></span>
                <div><strong>{loading ? '–' : managingCount}</strong><small>Managing</small></div>
              </button>
              <button type="button" className={`event-home__analytics-card ${relationshipFilter === 'attending' ? 'is-active' : ''}`} onClick={() => setRelationshipFilter('attending')} aria-pressed={relationshipFilter === 'attending'}>
                <span><UsersThree size={23} weight="fill" /></span>
                <div><strong>{loading ? '–' : attendingCount}</strong><small>Attending</small></div>
              </button>
            </div>
          </fieldset>
          <fieldset className="event-home__filter-group">
            <legend>Event timing</legend>
            <div>
              <button type="button" className={`event-home__analytics-card ${timingFilter === 'upcoming' ? 'is-active' : ''}`} onClick={() => setTimingFilter('upcoming')} aria-pressed={timingFilter === 'upcoming'}>
                <span><ClockCountdown size={23} weight="fill" /></span>
                <div><strong>{loading ? '–' : upcomingCount}</strong><small>Upcoming events</small></div>
              </button>
              <button type="button" className={`event-home__analytics-card ${timingFilter === 'past' ? 'is-active' : ''}`} onClick={() => setTimingFilter('past')} aria-pressed={timingFilter === 'past'}>
                <span><Stack size={23} weight="fill" /></span>
                <div><strong>{loading ? '–' : pastCount}</strong><small>Past events</small></div>
              </button>
            </div>
          </fieldset>
        </div>

        <div className="event-home__toolbar">
          <div className="event-home__filters">
            <label className="event-home__search">
              <MagnifyingGlass size={18} />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search events"
                aria-label="Search events"
              />
            </label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger className="event-home__type-filter" aria-label="Filter by event type">
                <FunnelSimple className="event-home__type-icon" size={17} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="event-home__type-menu" align="end">
                <SelectItem className="event-home__type-option" value="all">All event types</SelectItem>
                {getSupportedEventTypes().map((type) => (
                  <SelectItem className="event-home__type-option" key={type.id} value={type.id}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="event-home__layout-toggle" aria-label="Event card layout">
              <button type="button" className={layoutMode === 'grid' ? 'is-active' : ''} onClick={() => handleLayoutModeChange('grid')} aria-label="Grid view" title="Grid view">
                <SquaresFour size={20} weight="bold" />
              </button>
              <button type="button" className={layoutMode === 'list' ? 'is-active' : ''} onClick={() => handleLayoutModeChange('list')} aria-label="List view" title="List view">
                <ListBullets size={20} weight="bold" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="event-home__grid" aria-label="Loading events">
            {[1, 2, 3].map((item) => <div key={item} className="event-home__skeleton" />)}
          </div>
        ) : error ? (
          <div className="event-home__empty"><h2>We couldn’t load your events</h2><p>{error}</p></div>
        ) : filteredEvents.length ? (
          <div className={`event-home__grid is-${layoutMode}`}>
            {filteredEvents.map((event, eventIndex) => {
              const config = getEventTypeConfig(event.event_type);
              const fallbackCover = getDefaultCover(event.event_type);
              return (
                <div className="event-home__card-row" key={event.id}>
                <button
                  type="button"
                  className="event-home__card"
                  onClick={() => handleOpenEvent(event)}
                  disabled={openingEventId === event.id}
                >
                  <span className="event-home__cover">
                    <img
                      src={getEventCover(event)}
                      alt=""
                      onError={(imageEvent) => {
                        if (!imageEvent.currentTarget.src.endsWith(fallbackCover)) {
                          imageEvent.currentTarget.src = fallbackCover;
                        }
                      }}
                    />
                    <span className="event-home__type">{config.label}</span>
                  </span>
                  <span className="event-home__card-body">
                    <strong>{event.name}</strong>
                    <span><CalendarBlank size={16} /> {formatEventDate(event.start_date, event.end_date)}</span>
                    <span><MapPin size={16} /> {getEventLocation(event)}</span>
                      <span className="event-home__description">{getEventDetail(event, config.label)}</span>
                    <span className="event-home__open">
                      {openingEventId === event.id ? 'Opening…' : isManagingEvent(event) ? 'Open dashboard' : 'View event'}
                      <ArrowRight size={17} />
                    </span>
                  </span>
                </button>
                  <aside className="event-home__quote-card" aria-hidden="true">
                    <span>“</span>
                    <strong>{getEventTagline(eventIndex)}</strong>
                    <i />
                  </aside>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="event-home__empty">
            <h2>No matching events</h2>
            <p>{query || eventType !== 'all' ? 'Try clearing your search or filters.' : `You don’t have any ${timingFilter} events that you’re ${relationshipFilter}.`}</p>
          </div>
        )}
      </section>

      <Link to="/dashboard/events/new" className="event-home__create">
        <Plus size={22} weight="bold" />
        <span>Create event</span>
      </Link>
    </main>
  );
}
